import './work-task-planning.scss';
import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Scheduler, { Resource, SchedulerTypes, AppointmentDragging } from 'devextreme-react/scheduler';
import { useScreenSize } from '../../utils/media-query';
import { getWorkTaskPlanning, getUnscheduledWorkTasks } from '../../api/workTask';
import DataSource from 'devextreme/data/data_source';
import { useAuth } from '../../contexts/auth';
import { Popup, ToolbarItem } from 'devextreme-react/popup';
import { Appointment, WorkTaskForm, WorkTaskMap } from '../../components';
import ContextMenu, { type ContextMenuTypes } from 'devextreme-react/context-menu';
import ODataStore from 'devextreme/data/odata/store';
import axios from 'axios';
import { confirm } from 'devextreme/ui/dialog';
import moment from 'moment';
import Drawer from 'devextreme-react/drawer';
import ScrollView from 'devextreme-react/scroll-view';
import Draggable from 'devextreme-react/draggable';
import Button from 'devextreme-react/button';
type ContextMenuItem = ContextMenuTypes.Item & { onItemClick?: (e: ContextMenuTypes.ItemClickEvent) => void };
export const WorkTasksPlanning = React.memo(() => {
  const navigate = useNavigate();
  const { isXSmall } = useScreenSize();
  const { user, signOut } = useAuth();
  const schedulerRef = useRef<Scheduler>(null);
  const [workTaskPopupOpen, setWorkTaskPopup] = useState<boolean>(false);
  const [tasks, setTasks] = useState<DataSource>();
  const [employeeData, setEmployeeData] = useState<DataSource>();
  const [workTaskOid, setWorkTaskOid] = useState<string>('');
  const [appointmentData, setAppointmentData] = useState<Date[]>([]);
  const [visibleDateRange, setVisibleDateRange] = useState<{startDate: Date, endDate: Date} | null>({ startDate: new Date(), endDate: new Date() });
  const [contextMenuItems, setContextMenuItems] = useState<ContextMenuItem[]>([]);
  const [unscheduledTasks, setUnscheduledTasks] = useState<any[]>([]);
  const [drawerOpened, setDrawerOpened] = useState<boolean>(true);
  const [isUnscheduledMode, setIsUnscheduledMode] = useState<boolean>(false);
  const [mapOpened, setMapOpened] = useState<boolean>(false);
  const [mapFullWidth, setMapFullWidth] = useState<boolean>(false);
  const [rawTasks, setRawTasks] = useState<any[]>([]);

  // Memoize the ODataStore to prevent recreation on every render
  const storeWorkTask = useMemo(() => new ODataStore({
    key: 'Oid',
    keyType: 'Guid',
    url: `${process.env.REACT_APP_URL}/api/odata/WorkTask`,
    version: 4,
    beforeSend: function(e) {
      e.headers = {
        'Authorization': `Bearer ${user?.token}`
      };
    },
  }), [user?.token]);

  // Memoize views array to prevent recreation
  const views: SchedulerTypes.Properties['views'] = useMemo(() => [{
    type: 'day',
    name: 'Day',
    maxAppointmentsPerCell: 'unlimited',
  }, {
    type: 'workWeek',
    name: 'WorkWeek',
    groupOrientation: 'vertical',
    maxAppointmentsPerCell: 'unlimited',
  }, {
    type: 'month',
    name: 'Month',
    maxAppointmentsPerCell: 'unlimited',
  }], []);

  // Memoize the date range key to prevent unnecessary re-renders
  const dateRangeKey = useMemo(() => {
    if (!visibleDateRange) return null;
    return `${visibleDateRange.startDate.getTime()}-${visibleDateRange.endDate.getTime()}`;
  }, [visibleDateRange]);

  // 📦 Fetch tasks when visible date range changes (triggered after onContentReady)
  useEffect(() => {
    if (visibleDateRange && user?.token) {
      getWorkTaskPlanning(visibleDateRange?.startDate, visibleDateRange?.endDate, user.token).then(tasksList => {
        setRawTasks(tasksList || []);
        setTasks(new DataSource({
          store: tasksList,
          paginate: false
        }));
      });
    }
  }, [dateRangeKey, user?.token, visibleDateRange]);

  // 📦 Fetch unscheduled tasks
  const fetchUnscheduledTasks = useCallback(() => {
    if (user?.token) {
      getUnscheduledWorkTasks(user.token).then(tasksList => {
        setUnscheduledTasks(tasksList || []);
      }).catch(() => {
        setUnscheduledTasks([]);
      });
    }
  }, [user?.token]);

  useEffect(() => {
    fetchUnscheduledTasks();
  }, [fetchUnscheduledTasks]);

  // 📦 Initial data load and employee datasource setup
  useEffect(() => {
    setEmployeeData(new DataSource({
      store: {
        type: 'odata',
        version: 4,
        key: 'Oid',
        keyType: 'Guid',
        url: `${process.env.REACT_APP_URL}/api/odata/Employee`,
        errorHandler: (error) => {
          if (error.httpStatus === 401) {
            signOut(true);
          }
        },
        beforeSend: (e) => {
          e.headers = {
            'Authorization': `Bearer ${user?.token}`
          };
        }
      },
      select: [
        'Oid',
        'DateOfBirth',
        'Email',
        'PhoneNumber',
        'Address',
        'City',
        'PostalCode',
        'Position',
        'CreatedAt',
        'SchedulerColor',
        'FullName',
        'Active'
      ],
      expand: [],
      filter: ['Active', '=', true]
    }));
  }, [user?.token, signOut]);

  // Restore scheduler state from localStorage after mount
  useEffect(() => {
    const schedulerStorageKey = `schedulerState_${user?.tenantName || 'default'}`;
    const savedState = localStorage.getItem(schedulerStorageKey);
    if (savedState && schedulerRef.current) {
      try {
        const { currentDate, currentView } = JSON.parse(savedState);
        const instance = schedulerRef.current.instance;
        if (currentDate) {
          instance.option('currentDate', new Date(currentDate));
        }
        if (currentView) {
          instance.option('currentView', currentView);
        }
        // Clear saved state after restoring
        localStorage.removeItem(schedulerStorageKey);
      } catch (e) {
        // silent
      }
    }
  }, [tasks, employeeData]);

  const onContentReady = useCallback((e) => {

    // Capture initial visible date range
    const startViewDate = e.component.getStartViewDate();
    const endViewDate = e.component.getEndViewDate();

    // Set time to 00:00:00 for consistent comparisons
    const normalizedStartDate = new Date(startViewDate);
    normalizedStartDate.setHours(0, 0, 0, 0);

    const normalizedEndDate = new Date(endViewDate);
    normalizedEndDate.setHours(0, 0, 0, 0);

    // Only update if the dates actually changed
    setVisibleDateRange(prevRange => {
      if (!prevRange ||
          prevRange.startDate.getTime() !== normalizedStartDate.getTime() ||
          prevRange.endDate.getTime() !== normalizedEndDate.getTime()) {
        return {
          startDate: normalizedStartDate,
          endDate: normalizedEndDate
        };
      }
      return prevRange;
    });

  }, []);

  const onAppointmentUpdating = useCallback(async(e: SchedulerTypes.AppointmentUpdatingEvent) => {
    const toLocalIso = (date) => {
      if (!date) return undefined;
      const d = date instanceof Date ? date : new Date(date);
      const pad = (n) => n.toString().padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    };
    const sendEmail = await confirm('Wilt u een e-mailnotificatie naar de klant sturen?', 'E-mail versturen');
    const workTaskOid = e.newData.WorkTaskOid;
    await axios.put(`/api/WorkTask/Update/${workTaskOid}`, {
      TaskStartDate: toLocalIso(e.newData.StartDate),
      TaskEndDate: toLocalIso(e.newData.EndDate),
      SendEmail: sendEmail,
    }, {
      headers: { 'Authorization': `Bearer ${user?.token}` }
    });
    getWorkTaskPlanning(visibleDateRange?.startDate, visibleDateRange?.endDate, user?.token).then(tasksList => {
      setRawTasks(tasksList || []);
      setTasks(new DataSource({
        store: tasksList,
        paginate: false
      }));
    });
  }, [visibleDateRange?.startDate, visibleDateRange?.endDate, user?.token]);

  const onAppointmentDeleting = useCallback(async(e: SchedulerTypes.AppointmentDeletingEvent) => {
    await storeWorkTask.remove(e.appointmentData.WorkTaskOid);
    getWorkTaskPlanning(visibleDateRange?.startDate, visibleDateRange?.endDate, user?.token).then(tasksList => {
      setRawTasks(tasksList || []);
      setTasks(new DataSource({
        store: tasksList,
        paginate: false
      }));
    });
  }, [storeWorkTask, visibleDateRange?.startDate, visibleDateRange?.endDate, user?.token]);

  const onAppointmentFormOpening = useCallback((e: SchedulerTypes.AppointmentFormOpeningEvent) => {
    setWorkTaskOid(e.appointmentData?.WorkTaskOid);
    setWorkTaskPopup(true);
    setAppointmentData([e.appointmentData?.StartDate, e.appointmentData?.EndDate]);
    e.cancel = true;
  }, []);

  const workTaskHidePopup = useCallback(() => {
    setWorkTaskPopup(false);
    setWorkTaskOid('');
    setAppointmentData([]);
    setIsUnscheduledMode(false);
    getWorkTaskPlanning(visibleDateRange?.startDate, visibleDateRange?.endDate, user?.token).then(tasksList => {
      setRawTasks(tasksList || []);
      setTasks(new DataSource({
        store: tasksList,
        paginate: false
      }));
    });
    fetchUnscheduledTasks();
  }, [visibleDateRange?.startDate, visibleDateRange?.endDate, user?.token, fetchUnscheduledTasks]);

  // Repaint scheduler after layout change so events adjust to new width
  const repaintScheduler = useCallback(() => {
    setTimeout(() => {
      schedulerRef.current?.instance.repaint();
    }, 350); // wait for CSS transition to finish
  }, []);

  // Toggle drawer visibility
  const toggleDrawer = useCallback(() => {
    setDrawerOpened(prev => !prev);
    repaintScheduler();
  }, [repaintScheduler]);

  // Toggle map panel visibility
  const toggleMap = useCallback(() => {
    setMapOpened(prev => {
      if (prev) setMapFullWidth(false);
      return !prev;
    });
    repaintScheduler();
  }, [repaintScheduler]);

  // Toggle map full width
  const toggleMapFullWidth = useCallback(() => {
    setMapFullWidth(prev => !prev);
    repaintScheduler();
  }, [repaintScheduler]);

  // Navigate to task details from map popup
  const onMapTaskClick = useCallback((workTaskOid: string) => {
    // Save scheduler state before navigating
    if (schedulerRef.current) {
      const instance = schedulerRef.current.instance;
      const schedulerState = {
        currentDate: instance.option('currentDate'),
        currentView: instance.option('currentView')
      };
      localStorage.setItem(`schedulerState_${user?.tenantName || 'default'}`, JSON.stringify(schedulerState));
    }
    navigate('/worktask-details', { state: { workTaskOid, backTo: 'workTasksPlanning', relationOid: 'relationOid' } });
  }, [navigate, user?.tenantName]);

  // Open popup for creating unscheduled task
  const openUnscheduledTaskPopup = useCallback(() => {
    setIsUnscheduledMode(true);
    setWorkTaskOid('');
    setAppointmentData([]);
    setWorkTaskPopup(true);
  }, []);

  // Handle drag and drop from unscheduled list to scheduler
  const onAppointmentAdd = useCallback(async(e) => {
    // itemData is set in onDragStart to contain our task
    const taskData = e.itemData;
    if (!taskData || !taskData.WorkTaskOid) {
      return;
    }

    // Get the scheduler instance to access cell data
    const schedulerInstance = e.toComponent;

    // Get coordinates from the drop event
    const dropEvent = e.event;
    let startDate = new Date();
    let endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // 1 hour default

    try {
      // Try to get cell data from coordinates
      if (schedulerInstance && dropEvent) {
        const cellData = schedulerInstance.getTargetCellData(dropEvent);
        if (cellData) {
          startDate = cellData.startDate;
          endDate = cellData.endDate;
        }
      }
    } catch (error) {
      // silent
    }

    // Prevent the default add behavior (since we're opening a form instead)
    e.cancel = true;

    // Open the form popup with the task so user can assign employees
    setWorkTaskOid(taskData.WorkTaskOid);
    setAppointmentData([startDate, endDate]);
    setIsUnscheduledMode(false);
    setWorkTaskPopup(true);
  }, []);

  // Render function for the unscheduled tasks sidebar
  const renderUnscheduledTasksSidebar = useCallback(() => {
    return (
      <div className='unscheduled-tasks-sidebar'>
        <div className='sidebar-header'>
          <h3>Ongeplande taken</h3>
          <div className='header-actions'>
            <span className='task-count'>{unscheduledTasks.length}</span>
            <Button
              icon='add'
              hint='Nieuwe ongeplande taak'
              onClick={openUnscheduledTaskPopup}
              stylingMode='text'
            />
          </div>
        </div>
        <ScrollView height='calc(100% - 50px)' showScrollbar='onHover'>
          <div className='unscheduled-tasks-list' id='unscheduled-list'>
            {unscheduledTasks.map((task) => (
              <Draggable
                key={task.WorkTaskOid}
                group='appointmentsGroup'
                data={task}
                clone
                onDragStart={(e) => {
                  e.itemData = e.fromData;
                }}
                onDragEnd={(e) => {
                  if (e.toData) {
                    e.cancel = true;
                  }
                }}
              >
                <div
                  className='unscheduled-task-item dx-card'
                  onClick={() => {
                    setWorkTaskOid(task.WorkTaskOid);
                    setAppointmentData([]);
                    setIsUnscheduledMode(true);
                    setWorkTaskPopup(true);
                  }}
                >
                  <div className='task-relation'>{task.RelationName}</div>
                  <div className='task-description'>{task.TaskDescription}</div>
                  <div className='task-activity'>{task.ActivityName}</div>
                  <div className='task-status'>
                    {!task.HasDate && <span className='status-badge no-date'>Geen datum</span>}
                    {!task.HasEmployees && <span className='status-badge no-employee'>Geen medewerker</span>}
                  </div>
                </div>
              </Draggable>
            ))}
            {unscheduledTasks.length === 0 && (
              <div className='no-tasks-message'>Geen ongeplande taken</div>
            )}
          </div>
        </ScrollView>
      </div>
    );
  }, [unscheduledTasks, openUnscheduledTaskPopup]);

  // Memoize close button options to prevent recreation
  const closeButtonOptions = useMemo(() => ({
    text: 'Sluiten',
    onClick: workTaskHidePopup
  }), [workTaskHidePopup]);

  const customizeDateNavigatorText = useCallback((e) => {
    return `Week ${moment(e.startDate).week()}, ${moment(e.startDate).format('DD-MMM-yyyy')} - ${moment(e.endDate).format('DD-MMM-yyyy')}`;
  }, []);

  const onAppointmentContextMenu = useCallback((event: SchedulerTypes.AppointmentContextMenuEvent) => {
    const { appointmentData } = event;

    // Save current scheduler state to localStorage
    if (schedulerRef.current) {
      const instance = schedulerRef.current.instance;
      const schedulerState = {
        currentDate: instance.option('currentDate'),
        currentView: instance.option('currentView')
      };
      localStorage.setItem(`schedulerState_${user?.tenantName || 'default'}`, JSON.stringify(schedulerState));
    }

    setContextMenuItems([
      {
        text: 'Open opdracht',
        onItemClick: () => navigate('/worktask-details', { state: { workTaskOid: appointmentData.WorkTaskOid, backTo: 'workTasksPlanning', relationOid: 'relationOid' } }),
      },
    ]);
  }, [navigate]);

  const onContextMenuItemClick = (e: ContextMenuTypes.ItemClickEvent) => {
  (e.itemData as ContextMenuItem).onItemClick?.(e);
};

  // Memoize loading component to prevent recreation
  const LoadingComponent = useMemo(() => <div>Loading...</div>, []);

  return <div className='view-wrapper-calendar'>
    <div className='calendar-content'>
      <Button
        className='drawer-toggle-button'
        icon={drawerOpened ? 'chevrondoubleleft' : 'chevrondoubleright'}
        onClick={toggleDrawer}
        hint={drawerOpened ? 'Verberg ongeplande taken' : 'Toon ongeplande taken'}
      />
      <Button
        className={`map-toggle-button${mapOpened ? ' map-open' : ''}`}
        icon='map'
        onClick={toggleMap}
        hint={mapOpened ? 'Verberg kaart' : 'Toon kaart'}
      />
      <Drawer
        opened={drawerOpened}
        openedStateMode='shrink'
        position='left'
        revealMode='slide'
        component={renderUnscheduledTasksSidebar}
        closeOnOutsideClick={false}
        minSize={0}
        maxSize={280}
      >
        <div className={`scheduler-and-map${mapOpened ? ' map-visible' : ''}${mapFullWidth ? ' map-full-width' : ''}`}>
          <div className='main-content'>
            {tasks && employeeData ? (
              <Scheduler
                ref={schedulerRef}
                dataSource={tasks}
                timeZone='Europe/Amsterdam'
                adaptivityEnabled={isXSmall}
                allDayPanelMode='all'
                defaultCurrentView='workWeek'
                height='100%'
                currentView='workWeek'
                startDateExpr='StartDate'
                groupByDate={false}
                endDateExpr='EndDate'
                allDayExpr='IsAllDay'
                maxAppointmentsPerCell={100}
                showCurrentTimeIndicator
                startDayHour={7}
                endDayHour={18}
                textExpr='Text'
                appointmentComponent={Appointment}
                onAppointmentContextMenu={onAppointmentContextMenu}
                onContentReady={onContentReady}
                onAppointmentUpdating={onAppointmentUpdating}
                onAppointmentFormOpening={onAppointmentFormOpening}
                onAppointmentDeleting={onAppointmentDeleting}
                customizeDateNavigatorText={customizeDateNavigatorText}
                views={views}>
                <AppointmentDragging
                  group='appointmentsGroup'
                  onAdd={onAppointmentAdd}
                />
                <Resource
                  dataSource={employeeData}
                  fieldExpr='EmployeeOid'
                  valueExpr='Oid'
                  displayExpr='FullName'
                  colorExpr='SchedulerColor'
                  label='Medewerker'
                />
              </Scheduler>
            ) : (
              LoadingComponent
            )}
            <ContextMenu
              dataSource={contextMenuItems}
              width={200}
              target='.dx-scheduler-appointment'
              onItemClick={onContextMenuItemClick}
            />
            <Popup
              visible={workTaskPopupOpen}
              dragEnabled={false}
              hideOnOutsideClick
              showCloseButton={false}
              showTitle
              title={isUnscheduledMode ? 'Ongeplande taak aanmaken' : 'Opdracht aanmaken'}
              width={600}
              height={650}>
              <ToolbarItem
                widget='dxButton'
                toolbar='top'
                location='after'
                options={closeButtonOptions} />
              <WorkTaskForm
                appointmentData={appointmentData}
                workTaskOid={workTaskOid}
                contractOid=''
                onComplete={workTaskHidePopup}
                allowUnscheduled={isUnscheduledMode} />
            </Popup>
          </div>
          {mapOpened && (
            <div className='map-panel'>
              <WorkTaskMap tasks={rawTasks} onTaskClick={onMapTaskClick} isFullWidth={mapFullWidth} onToggleFullWidth={toggleMapFullWidth} />
            </div>
          )}
        </div>
      </Drawer>
    </div>
  </div>;
});
